<template>
  <div class="c-shared-with-you flex flex-1 flex-col">
    <PartnerDataSharing
      :partner-populations-with-metadata="partnerPopulationsWithEmpty"
      @open-data-share-request-modal="
        (population) =>
          openDataShareModal(population as PartnerPopulationWithEmptyRequired)
      "
    />
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef } from 'vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import PartnerDataSharing from '@/components/partners/PartnerDataSharing.vue';

import usePartnerSharing from '@/composables/usePartnerSharing';
import {
  STANDARD_POPULATION_TYPES,
  emptyPopulation,
} from '@/constants/standard_populations';
import { useDataRequestsStore, usePopulationsStore } from '@/stores';
import type { PartnerPopulationWithMetadata } from '@/types/data_share_requests';
import {
  type SegmentType,
  type StandardPopulationType,
} from '@/types/populations';

type PartnerPopulationWithEmptyOptional = PartnerPopulationWithMetadata & {
  isEmpty?: boolean;
};

type PartnerPopulationWithEmptyRequired = PartnerPopulationWithMetadata & {
  isEmpty: boolean;
};

const emit = defineEmits<{
  'open-data-share-request-modal': [
    data: {
      orgId: number;
      partnerPop: PartnerPopulationWithEmptyRequired;
    },
  ];
}>();

const route = useRoute();
const injectedPartnerId = inject<ComputedRef<number | null>>(
  'propsPartnerId',
  computed(() => null),
);
const partnerOrgId = ref(
  injectedPartnerId.value ?? parseInt(route.params.partner_org_id as string),
);
const { partnerPopulationsWithMetadata } = usePartnerSharing(
  partnerOrgId.value,
);
const { refreshDataRequestsStore } = useDataRequestsStore();

function createEmptyPartnerPopulation(
  standardType: StandardPopulationType,
): PartnerPopulationWithEmptyRequired {
  const emptyPop = emptyPopulation(standardType);
  return {
    ...emptyPop,
    isEmpty: true,
    id: -1,
    organization_id: partnerOrgId.value,
    population_type: null,
    segment: standardType as SegmentType,
    integration_type: '',
    requestDataIsVisible: false,
    dataRequestInformation: undefined,
    populationToPopulationSettings: [],
    dataShare: {
      visibility: 'hidden' as const,
    },
    visibilityText: {
      title: 'Hidden',
      value: 'hidden',
      subtitle: () => '',
    },
    sourceFields: [],
  } as PartnerPopulationWithEmptyRequired;
}

const partnerPopulationsWithEmpty = computed(() => {
  const populations: PartnerPopulationWithEmptyOptional[] =
    partnerPopulationsWithMetadata.value.map((pop) => ({
      ...pop,
      isEmpty: false,
    })) as PartnerPopulationWithEmptyOptional[];

  STANDARD_POPULATION_TYPES.forEach((standardType) => {
    const exists = populations.some(
      (pop) => pop.standard_type === standardType,
    );

    if (!exists) {
      populations.push(
        createEmptyPartnerPopulation(standardType as StandardPopulationType),
      );
    }
  });

  return populations.sort((a, b) => {
    const aIsStandard = !!a.standard_type;
    const bIsStandard = !!b.standard_type;

    if (aIsStandard && bIsStandard) {
      const aIndex = STANDARD_POPULATION_TYPES.indexOf(
        a.standard_type as StandardPopulationType,
      );
      const bIndex = STANDARD_POPULATION_TYPES.indexOf(
        b.standard_type as StandardPopulationType,
      );
      return aIndex - bIndex;
    }

    if (aIsStandard && !bIsStandard) return -1;
    if (!aIsStandard && bIsStandard) return 1;

    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
});

onMounted(async () => {
  await refreshDataRequestsStore();
  if (route.query?.partnerPopId) {
    const populationsStore = usePopulationsStore();
    const population = populationsStore.getPartnerPopulationById(
      Number(route.query.partnerPopId),
    );
    if (population) {
      openDataShareModal({
        ...population,
        isEmpty: false,
      } as PartnerPopulationWithEmptyRequired);
    }
  }
});

function openDataShareModal(population: PartnerPopulationWithEmptyRequired) {
  emit('open-data-share-request-modal', {
    orgId: partnerOrgId.value,
    partnerPop: population,
  });
}
</script>
